<i18n>
{
  "en": {
    "label": {
      "email": "Email",
      "password": "Password",
      "passwordConfirm": "Password(Confirm)",
      "PPcheckbox": "Confirm to our Privacy Policy",
      "submitBtn": "Send Email",
      "cookieAccept": "I agree to use Cookie to continue.",
      "link": "Link"
    },
    "PP": {
      "linkName": "If you reset password, please click ",
      "accept": "accept",
      "openWithNewTab": "Opens in new tab"
    },
    "message": {
      "progress": "processing...",
      "emailSend": "Success! We send email to {address}. Please confirm your email box and click the link.",
      "unexpectedError": "Unexpected error was happened."
    }
  },
  "ja": {
    "label": {
      "email": "メールアドレス",
      "password": "パスワード(8文字以上)",
      "passwordConfirm": "パスワード(確認)",
      "PPcheckbox": "個人情報の取り扱いに同意する",
      "submitBtn": "認証メール送信",
      "cookieAccept": "機能実現のためCookieを利用することに同意する",
      "link": "リンク"
    },
    "PP": {
      "linkName": "パスワードを忘れた方は",
      "accept": "こちら",
      "openWithNewTab": "別タブで閲覧する"
    },
    "message": {
      "progress": "処理中…",
      "emailSend": "{address} にメールを送信しました！ メールボックスをご確認の上、メール内のリンクのクリックをお願いします。なお、メールが届かない場合、迷惑メールフォルダに分類されている可能性もあります。迷惑メールフォルダの中もご確認ください。",
      "unexpectedError": "予期せぬエラーが発生しました。お手数ですが、時間を空けて再度お試しいただくか、端末やブラウザなどの環境を変えてお試しください。"
    }
  }
}
</i18n>

<template>
  <div style="width: 100%; !important">
    <v-container>
      <v-layout wrap justify-center text-xs-center>
        <v-flex>
          <form v-if="!inProgress && !completed" @submit.prevent>
            <div>
              <TextInput
                name="email"
                v-model="email"
                :width="inputText.width"
                :color="inputText.color"
                :placeholderValue="this.$t('label.email')"
                v-validate="'required|email'"
                :readonly="inProgress"
              />

              <TextInput
                name="password"
                type="password"
                :width="inputText.width"
                :color="inputText.color"
                v-model="password"
                :placeholderValue="this.$t('label.password')"
                v-validate.disable="{ required: true, min: 8 }"
                :readonly="inProgress"
              />

              <TextInput
                name="passwordConfirm"
                type="password"
                :width="inputText.width"
                :color="inputText.color"
                :placeholderValue="this.$t('label.passwordConfirm')"
                v-model="passwordConfirm"
                v-validate.disable="{ is: password }"
                :readonly="inProgress"
              />

              <v-checkbox
                name="PPconfirm"
                v-model="PPconfirm"
                style="text-align: left"
                v-validate.continues="'required:true'"
                :readonly="inProgress"
              >
                <div slot="label">
                  <span style="color: #353535;">
                    {{ $t('label.PPcheckbox') }}（
                    <a
                      slot="activator"
                      target="_blank"
                      :href="privacyPolicyUrl"
                      @click.stop
                      >{{ $t('label.link') }}</a
                    >）
                  </span>
                </div>
              </v-checkbox>

              <v-checkbox
                name="CookieAccept"
                v-model="CookieAccept"
                style="text-align: left"
                v-validate.continues="'required:true'"
                :readonly="inProgress"
              >
                <div slot="label">
                  <span style="color: #353535;">{{
                    $t('label.cookieAccept')
                  }}</span>
                </div>
              </v-checkbox>

              <SubmitBtn
                :disabled="inProgress"
                :colorCode="submitBtn.colorCode"
                @click="submitted"
                :autoSpread="true"
                :isDark="true"
                :hasOutline="false"
              >
                <strong>{{ $t('label.submitBtn') }}</strong>
              </SubmitBtn>

              <v-alert outline :value="errorMessage">{{
                errorMessage
              }}</v-alert>
              <br />
              <span v-for="(error, key, index) in errors.all()" :key="index">
                <p class="vee-error" style="color: #F87060">{{ error }}</p>
              </span>

              <br />
              <span>
                ※「noreply@tokyocameraclub.com」からメールが送信されます。迷惑メールと認識されないようにドメイン設定をご確認ください。
              </span>
            </div>
          </form>
          <v-alert
            :value="true"
            type="info"
            v-if="inProgress"
            class="inProgress"
          >
            {{ progressMessage }}
          </v-alert>

          <div
            class="successInfo"
            v-if="completed"
          >
            {{ completeMessage }}
          </div>

          <v-alert :value="errorMessages" type="error">
            <span v-for="error in errorMessages" :key="error.code">
              <span>{{ error.code }}</span
              ><br />
            </span>
          </v-alert>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
'use strict';
import SubmitBtn from '@/components/atoms/SubmitBtn.vue';
import TextInput from '@/components/atoms/TextInput.vue';
import { Auth } from '@/api';

export default {
  components: {
    SubmitBtn,
    TextInput
  },
  props: {
    onSubmit: {
      type: Function,
      required: true
    },
    sourceType: {
      type: Number
    },
    privacyPolicyUrl: {
      type: String
    }
  },
  methods: {
    processingOn(message) {
      this.inProgress = true;
      this.progressMessage = message;
      this.errorMessage = null;
      this.errorMessages = null;
    },
    processingOff() {
      this.inProgress = false;
      this.progressMessage = null;
    },
    submitted() {
      this.$validator.validate().then(result => {
        if (result !== true) {
          return;
        } // validationの結果をハンドリング
        this.processingOn(this.$t('message.progress'));
        this.$nextTick(function() {
          // 処理中フラグによるDOM制御を待つ
          Auth.verifyEmail({
            email: this.email,
            password: this.password,
            isConsentToPrivacyPolicy: this.PPconfirm,
            signupToken: this.$store.state.signupToken
          })
            .then(() => {
              this.processingOff();
              this.completed = true;
              this.completeMessage = this.$t('message.emailSend', {
                address: this.email
              });
            })
            .catch(ApiError => {
              this.processingOff();
              if (ApiError.errors) {
                this.errorMessages = ApiError.errors;
              } else {
                this.errorMessage = this.$t('message.unexpectedError');
              }
            });
        });
      });
    }
  },
  data() {
    return {
      inProgress: false,
      progressMessage: '',
      completed: false,
      completeMessage: '',
      submitBtn: {
        colorCode: 'primary'
      },
      inputText: {
        width: '100%',
        color: 'white'
      },
      PPconfirm: false,
      email: '',
      password: '',
      passwordConfirm: '',
      errorMessage: '',
      CookieAccept: false,
      errorMessages: null
    };
  }
};
</script>

<style scoped lang="stylus">
div.v-input__slot {
  border-radius: 4px !important;
}
.successInfo {
  margin: 0px;
  padding: 16px;
  text-align: left;
  color: white;
  background-color: #4caf50;
  word-wrap: break-word;
  box-shadow: 2px 2px 2px 2px rgba(0,0,0,0.4) inset;
}
</style>
